import React, { useState, useEffect } from "react";
import { Button, withField, Select, Tooltip } from "@douyinfe/semi-ui";
import { VHH_DESIGN_TYPES } from "@services/home/config/home.config";
import { DataTypeModel } from "@services/home/model/home.model";
import { VhhDesignTypeEnum } from "@services/home/enum/home.enum";
import { ReactComponent as BulbIcon } from "@assets/icons/bulb_icon.svg";
import type { RenderSelectedItemFn } from "@douyinfe/semi-ui/lib/es/select";
import { useTranslation } from "react-i18next";

import styles from "./index.module.scss";
import classNames from "classnames";

interface Prop {
  value?: any;
  onChange?: (values: any) => void;
}

const VhhDesignAttachmentField: React.FC<Prop> = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();

  const getAntibodyInputType = (type: VhhDesignTypeEnum): string =>
    t(`antibodyInputType.${type}`);

  const [selectedType, setSelectedType] = useState<string>(
    VhhDesignTypeEnum.PdbID
  );

  const [isDeepThink, setIsDeepThink] = useState(false);

  function onToggleDeepThink() {
    if (!onChange) {
      return;
    }

    setIsDeepThink(!isDeepThink);

    onChange({
      protein_type: selectedType,
      deep_think: !isDeepThink,
    });
  }

  function onTypeChange(
    v: string | number | any[] | Record<string, any> | undefined
  ) {
    if (!onChange || typeof v !== "string") {
      return;
    }

    setSelectedType(v);

    onChange({
      protein_type: v,
      deep_think: isDeepThink,
    });
  }

  const renderSelectedItem: RenderSelectedItemFn = (
    optionNode: Record<string, any>
  ) => {
    return optionNode.label;
  };

  const renderCustomOption = (item: DataTypeModel) => {
    const name = getAntibodyInputType(item.value as VhhDesignTypeEnum);
    return (
      <Select.Option
        value={item.value}
        showTick={false}
        key={`${item.value}-${name}`}
      >
        <item.icon className={styles.typeIcon} />
        <div>{name}</div>
      </Select.Option>
    );
  };

  useEffect(() => {
    if (!value) {
      if (onChange) {
        onChange({
          protein_type: selectedType,
          deep_think: isDeepThink,
        });
      }
    }
  }, [value]);

  return (
    <div className={styles.attachmentContainer}>
      <Tooltip
        content={t("homePage.attachment.inputTooltip")}
        style={{ maxWidth: "unset" }}
      >
        <Select
          clickToHide
          className="select-btn"
          size="small"
          onChange={onTypeChange}
          defaultValue={VhhDesignTypeEnum.PdbID}
          renderSelectedItem={renderSelectedItem}
          dropdownClassName="select-dropdown"
        >
          {VHH_DESIGN_TYPES.map((item) => {
            return renderCustomOption(item);
          })}
        </Select>
      </Tooltip>

      <Tooltip
        content={t("homePage.attachment.evolutionTooltip")}
        style={{ maxWidth: "unset" }}
      >
        <Button
          style={{ marginLeft: "16px" }}
          onClick={onToggleDeepThink}
          className={classNames({
            "border-btn": true,
            active: isDeepThink,
          })}
          theme="solid"
          type="tertiary"
          size="small"
          icon={<BulbIcon />}
        >
          {t("homePage.attachment.evolution")}
        </Button>
      </Tooltip>
    </div>
  );
};

export default withField(VhhDesignAttachmentField);
